import React, { createContext, useContext, useState, useEffect } from 'react';
import DustTransition from '../components/DustTransition';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'system';
  });

  const [actualTheme, setActualTheme] = useState('light');
  const [isDusting, setIsDusting] = useState(false);
  const [transitionTheme, setTransitionTheme] = useState('light'); // Theme interacting with dust

  useEffect(() => {
    const applyTheme = () => {
      let themeToApply = theme;

      if (theme === 'system') {
        const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        themeToApply = systemPrefersDark ? 'dark' : 'light';
      }

      setActualTheme(themeToApply);
      document.documentElement.setAttribute('data-theme', themeToApply);
      localStorage.setItem('theme', theme);
    };

    applyTheme();

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        applyTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]); // Removed isDusting dependency as we want theme to update under the dust

  const changeTheme = (newTheme) => {
    if (newTheme === theme) return;
    
    // 1. Capture current theme for the dust effect
    setTransitionTheme(actualTheme);
    
    // 2. Start dusting
    setIsDusting(true);

    // 3. Delay the actual theme switch slightly so the overlay covers the screen first
    setTimeout(() => {
        setTheme(newTheme);
    }, 100); 
  };

  const handleDustComplete = () => {
    setIsDusting(false);
  };

  return (
    <ThemeContext.Provider value={{ theme, actualTheme, changeTheme }}>
      {children}
      <DustTransition 
        isDusting={isDusting} 
        onComplete={handleDustComplete}
        themeColor={transitionTheme} 
      />
    </ThemeContext.Provider>
  );
};

